import React, { useState, useEffect, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { Button } from "@mui/material";
import {
  QrCodeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import ModernProductSelector from "./Selector/ModernProductSelector";
import { fetchProducts } from "../../api/product";
import toast from "react-hot-toast";
import QRCodeScanner from "./QRCodeScanner";
import ReportAddItemsDialog from "../dashboard/service/reports/report-add-items-dialog";

/**
 * Reusable Add Product Dialog Component
 * 
 * @param {boolean} open - Whether dialog is open
 * @param {Function} onClose - Callback when dialog closes
 * @param {Function} onProductSelected - Callback when product is selected (product, additionalData) => void
 * @param {boolean} multiple - Allow multiple product selection
 * @param {string} title - Dialog title
 * @param {Function} renderAdditionalFields - Function to render additional fields (selectedProducts, formData, setFormData) => ReactNode
 * @param {Object} initialFormData - Initial form data for additional fields
 * @param {Function} validateFormData - Validation function (formData) => { valid: boolean, errors: object }
 * @param {boolean} showBulkAdd - Show bulk add option
 * @param {Function} onBulkAdd - Callback for bulk add
 * @param {boolean} showManualProduct - Show manual product option
 * @param {Function} onManualProduct - Callback for manual product (formData) => void
 */
export const AddProductDialog = ({
  open,
  onClose,
  onProductSelected,
  multiple = false,
  title = "Legg til produkt",
  renderAdditionalFields,
  initialFormData = {},
  validateFormData,
  showBulkAdd = false,
  onBulkAdd,
  showManualProduct = false,
  onManualProduct,
}) => {
  const { t } = useTranslation();
  const [qrScanMode, setQrScanMode] = useState(false);
  const [manualSearchMode, setManualSearchMode] = useState(false);
  const [manualProductMode, setManualProductMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [isSearching, setIsSearching] = useState(false);
  const [bulkAddDialogOpen, setBulkAddDialogOpen] = useState(false);
  const [productSelectorDialogOpen, setProductSelectorDialogOpen] = useState(false);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setQrScanMode(false);
      setManualSearchMode(false);
      setManualProductMode(false);
      setSelectedProducts([]);
      setFormData(initialFormData);
      setBulkAddDialogOpen(false);
      setProductSelectorDialogOpen(false);
    }
  }, [open, initialFormData]);

  const handleQrCodeScan = async (scannedCode) => {
    if (!scannedCode || scannedCode.trim() === "") {
      toast.error("Skriv inn eller skann en QR-kode");
      return;
    }

    if (isSearching) return;
    setIsSearching(true);

    try {
      // Try to search by product ID if it's a number
      let response = null;
      
      if (/^\d+$/.test(scannedCode.trim())) {
        try {
          response = await fetchProducts({
            ids: scannedCode.trim(),
            page: 1,
            per_page: 1,
            fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
          });
        } catch (error) {
          console.log("Product ID search failed, trying query search");
        }
      }
      
      // If ID search didn't work, search by query
      if (!response || !response.data || response.data.length === 0) {
        response = await fetchProducts({
          query: scannedCode.trim(),
          page: 1,
          per_page: 10,
          fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
        });
      }
      
      if (response && response.data && response.data.length > 0) {
        const product = response.data[0];
        if (multiple) {
          setSelectedProducts([...selectedProducts, product]);
        } else {
          setSelectedProducts([product]);
        }
      } else {
        toast.error("Produkt ikke funnet. Prøv å søke manuelt.");
      }
    } catch (error) {
      console.error("Error searching product:", error);
      toast.error("Kunne ikke søke etter produkt");
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.error("Velg minst ett produkt");
      return;
    }

    // Validate additional fields if validator provided
    if (validateFormData) {
      const validation = validateFormData(formData);
      if (!validation.valid) {
        Object.values(validation.errors).forEach((error) => {
          if (error) toast.error(error);
        });
        return;
      }
    }

    try {
      if (multiple) {
        await onProductSelected(selectedProducts, formData);
      } else {
        await onProductSelected(selectedProducts[0], formData);
      }
      
      // Close dialog on success
      onClose();
    } catch (error) {
      console.error("Error adding products:", error);
      // Error handling is done by onProductSelected
    }
  };

  const handleManualProduct = async (values) => {
    if (onManualProduct) {
      try {
        await onManualProduct(values);
        onClose();
      } catch (error) {
        console.error("Error adding manual product:", error);
      }
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as="div"
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    {title}
                  </Dialog.Title>

                  {!qrScanMode && !manualSearchMode && !manualProductMode && (
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => setQrScanMode(true)}
                        className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <QrCodeIcon className="h-5 w-5 mr-3 text-gray-600" />
                        <span className="text-gray-700 font-medium">
                          {t("project.item_list.scan_qr") || "Skann QR-kode"}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setProductSelectorDialogOpen(true);
                        }}
                        className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                      >
                        <MagnifyingGlassIcon className="h-5 w-5 mr-3 text-gray-600" />
                        <span className="text-gray-700 font-medium">
                          {t("project.item_list.manual_search") || "Søk manuelt"}
                        </span>
                      </button>
                      {showManualProduct && (
                        <button
                          type="button"
                          onClick={() => setManualProductMode(true)}
                          className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        >
                          <PlusIcon className="h-5 w-5 mr-3 text-gray-600" />
                          <span className="text-gray-700 font-medium">Legg til manuelt produkt</span>
                        </button>
                      )}
                      {showBulkAdd && (
                        <button
                          type="button"
                          onClick={() => {
                            setBulkAddDialogOpen(true);
                          }}
                          className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        >
                          <PlusIcon className="h-5 w-5 mr-3 text-gray-600" />
                          <span className="text-gray-700 font-medium">Legg til produkter i bulk</span>
                        </button>
                      )}
                    </div>
                  )}

                  {/* QR Code Scan Mode */}
                  {qrScanMode && (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            setQrScanMode(false);
                          }}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          ← Tilbake
                        </button>
                      </div>
                      <QRCodeScanner
                        open={qrScanMode}
                        onScan={handleQrCodeScan}
                        onBack={() => setQrScanMode(false)}
                        placeholder="Skriv produktnummer eller skann QR-kode"
                      />
                      {selectedProducts.length > 0 && (
                        <div className="mt-4">
                          <div className="text-sm font-medium text-gray-700 mb-2">
                            Valgt produkt:
                          </div>
                          {selectedProducts.map((product) => (
                            <div key={product.id} className="p-2 bg-blue-50 rounded mb-2">
                              {product.displayTitle || product.title}
                            </div>
                          ))}
                          {renderAdditionalFields && renderAdditionalFields(selectedProducts, formData, setFormData)}
                          <div className="mt-4 flex space-x-2">
                            <Button
                              variant="contained"
                              onClick={handleAddProducts}
                              className="flex-1"
                              disabled={isSearching}
                            >
                              Legg til
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setSelectedProducts([]);
                                setFormData(initialFormData);
                              }}
                            >
                              Nullstill
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}


                  {/* Manual Product Mode */}
                  {manualProductMode && onManualProduct && (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            setManualProductMode(false);
                          }}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          ← Tilbake
                        </button>
                      </div>
                      {renderAdditionalFields && renderAdditionalFields([], formData, setFormData, true)}
                    </div>
                  )}

                  {!qrScanMode && !manualProductMode && (
                    <div className="mt-4 flex justify-end">
                      <Button variant="outlined" onClick={onClose}>
                        Lukk
                      </Button>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>

      {/* Product Selector Dialog */}
      <Transition.Root show={productSelectorDialogOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setProductSelectorDialogOpen(false)}>
          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as="div"
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                  <div className="mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {t("project.item_list.manual_search") || "Søk manuelt"}
                    </Dialog.Title>
                  </div>
                  <ModernProductSelector
                    open={true}
                    selectedProducts={selectedProducts}
                    onChange={(products) => {
                      setSelectedProducts(products);
                    }}
                    onClose={() => {
                      setProductSelectorDialogOpen(false);
                    }}
                    multiple={multiple}
                    filters={[]}
                    showHeader={true}
                  />
                  {selectedProducts.length > 0 && (
                    <div className="mt-4 flex space-x-2">
                      <Button
                        variant="contained"
                        onClick={() => {
                          handleAddProducts();
                          setProductSelectorDialogOpen(false);
                        }}
                        className="flex-1"
                      >
                        Legg til {multiple ? `(${selectedProducts.length})` : ""}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setSelectedProducts([]);
                          setFormData(initialFormData);
                        }}
                      >
                        Nullstill
                      </Button>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Bulk Add Items Dialog */}
      {showBulkAdd && (
        <ReportAddItemsDialog
          open={bulkAddDialogOpen}
          onClose={() => {
            setBulkAddDialogOpen(false);
          }}
          onSave={(itemsToAdd) => {
            if (onBulkAdd) {
              onBulkAdd(itemsToAdd);
            } else if (onProductSelected) {
              // Fallback: call onProductSelected for each item
              itemsToAdd.forEach((item) => {
                onProductSelected(item, formData);
              });
            }
            setBulkAddDialogOpen(false);
            onClose();
          }}
          report={null}
          onSubDialogStateChange={() => {}}
        />
      )}
    </Transition.Root>
  );
};

